import { useState } from 'react';
import type { AdminWorldState } from '../../../lib/api';
import { JsonViewer } from './JsonViewer';

type CanonTab = 'entities' | 'relationships' | 'timeline' | 'threads' | 'constraints' | 'raw';

interface WorldCanonProps {
  state: AdminWorldState;
  subSection?: string;
}

export function WorldCanon({ state, subSection }: WorldCanonProps) {
  const [activeTab, setActiveTab] = useState<CanonTab>(() => {
    const validTabs: CanonTab[] = ['entities', 'relationships', 'timeline', 'threads', 'constraints', 'raw'];
    return validTabs.includes(subSection as CanonTab) ? (subSection as CanonTab) : 'entities';
  });

  const canon = state.canon as {
    entities?: {
      characters?: Record<string, { name: string; status?: string; traits?: string[] }>;
      places?: Record<string, { name: string; description?: string }>;
      factions?: Record<string, { name: string; description?: string }>;
      items?: Record<string, { name: string; description?: string }>;
    };
    relationships?: Array<{ a: string; b: string; type: string; strength: number }>;
    timeline?: {
      t_current: string;
      events: Record<string, { title: string; participants: string[]; day_index?: number }>;
    };
    threads?: {
      open?: Record<string, { title: string; stakes?: string }>;
      closed?: Record<string, { title: string; resolution?: string }>;
    };
  } | null;

  const constraints = state.constraints as unknown[] | null;

  const tabs: Array<{ id: CanonTab; label: string; count?: number }> = [
    {
      id: 'entities',
      label: 'Entities',
      count: canon
        ? Object.keys(canon.entities?.characters ?? {}).length +
          Object.keys(canon.entities?.places ?? {}).length +
          Object.keys(canon.entities?.factions ?? {}).length +
          Object.keys(canon.entities?.items ?? {}).length
        : 0,
    },
    {
      id: 'relationships',
      label: 'Relationships',
      count: Array.isArray(canon?.relationships) ? canon.relationships.length : 0,
    },
    {
      id: 'timeline',
      label: 'Timeline',
      count: Object.keys(canon?.timeline?.events ?? {}).length,
    },
    {
      id: 'threads',
      label: 'Threads',
      count: Object.keys(canon?.threads?.open ?? {}).length + Object.keys(canon?.threads?.closed ?? {}).length,
    },
    {
      id: 'constraints',
      label: 'Constraints',
      count: Array.isArray(constraints) ? constraints.length : constraints ? 1 : 0,
    },
    { id: 'raw', label: 'Raw JSON' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-xl text-parchment">Canon</h1>
        <p className="text-mist text-sm mt-1">World state version {state.version}</p>
      </div>

      {/* Tabs */}
      <div className="bg-night border border-stone/30 rounded-xl overflow-hidden">
        <div className="flex border-b border-stone/30 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-rune/20 text-rune border-b-2 border-rune'
                  : 'text-mist hover:text-parchment hover:bg-stone/20'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="text-xs opacity-60">({tab.count})</span>
              )}
            </button>
          ))}
        </div>

        <div className="p-4 sm:p-6">
          {activeTab === 'entities' && (
            <EntitiesView entities={canon?.entities ?? {}} />
          )}

          {activeTab === 'relationships' && (
            <RelationshipsView relationships={canon?.relationships ?? []} />
          )}

          {activeTab === 'timeline' && (
            <TimelineView timeline={canon?.timeline ?? { t_current: '', events: {} }} />
          )}

          {activeTab === 'threads' && (
            <ThreadsView threads={canon?.threads ?? {}} />
          )}

          {activeTab === 'constraints' && (
            <ConstraintsView constraints={constraints} />
          )}

          {activeTab === 'raw' && (
            <div className="text-xs font-mono overflow-auto max-h-[60vh]">
              <JsonViewer data={state.canon} expandAll />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EntitiesView({ entities }: { entities: Record<string, Record<string, unknown>> }) {
  const categories = [
    { key: 'characters', label: 'Characters', items: entities.characters ?? {} },
    { key: 'places', label: 'Places', items: entities.places ?? {} },
    { key: 'factions', label: 'Factions', items: entities.factions ?? {} },
    { key: 'items', label: 'Items', items: entities.items ?? {} },
  ];

  return (
    <div className="space-y-6">
      {categories.map(({ key, label, items }) => {
        const entries = Object.entries(items);
        if (entries.length === 0) return null;

        return (
          <div key={key}>
            <h3 className="font-display text-parchment mb-3">{label} ({entries.length})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {entries.map(([id, entity]) => (
                <EntityCard key={id} id={id} entity={entity as Record<string, unknown>} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EntityCard({ id, entity }: { id: string; entity: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(false);
  const name = entity.name as string || id;
  const status = entity.status as string | undefined;
  const description = entity.description as string | undefined;
  const traits = entity.traits as string[] | undefined;

  return (
    <div className="bg-void border border-stone/30 rounded-lg p-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-parchment text-sm font-medium">{name}</div>
          <div className="text-mist/60 text-xs font-mono">{id}</div>
        </div>
        {status && (
          <span className={`px-2 py-0.5 rounded text-xs ${
            status === 'alive' ? 'bg-success/20 text-success' :
            status === 'dead' ? 'bg-ember/20 text-ember' :
            'bg-stone/30 text-mist'
          }`}>
            {status}
          </span>
        )}
      </div>
      {description && (
        <div className="text-mist text-xs mb-2 line-clamp-2">{description}</div>
      )}
      {traits && traits.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {traits.slice(0, 3).map((trait, i) => (
            <span key={i} className="px-1.5 py-0.5 rounded text-xs bg-stone/40 text-mist">
              {trait}
            </span>
          ))}
          {traits.length > 3 && (
            <span className="text-xs text-mist/60">+{traits.length - 3}</span>
          )}
        </div>
      )}
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-xs text-rune hover:underline"
      >
        {expanded ? 'Hide details' : 'Show details'}
      </button>
      {expanded && (
        <div className="mt-2 pt-2 border-t border-stone/30 text-xs overflow-auto max-h-48">
          <JsonViewer data={entity} depth={1} />
        </div>
      )}
    </div>
  );
}

function RelationshipsView({ relationships }: { relationships: Array<{ a: string; b: string; type: string; strength: number }> }) {
  if (relationships.length === 0) {
    return <div className="text-mist text-sm text-center py-8">No relationships defined</div>;
  }

  return (
    <div className="space-y-2">
      {relationships.map((rel, i) => (
        <div key={i} className="flex items-center gap-3 p-3 bg-void rounded-lg border border-stone/30">
          <span className="text-parchment text-sm font-mono">{rel.a}</span>
          <div className="flex-1 flex items-center gap-2">
            <div className="flex-1 h-1 bg-stone/30 rounded-full">
              <div
                className="h-full bg-rune rounded-full"
                style={{ width: `${Math.min(100, rel.strength * 100)}%` }}
              />
            </div>
            <span className="text-xs text-mist px-2 py-0.5 bg-stone/40 rounded">{rel.type}</span>
          </div>
          <span className="text-parchment text-sm font-mono">{rel.b}</span>
        </div>
      ))}
    </div>
  );
}

function TimelineView({ timeline }: { timeline: { t_current: string; events: Record<string, { title: string; participants: string[]; day_index?: number }> } }) {
  const events = Object.entries(timeline.events);

  return (
    <div className="space-y-4">
      {timeline.t_current && (
        <div className="bg-rune/10 border border-rune/30 rounded-lg p-3">
          <span className="text-xs text-mist">Current Time: </span>
          <span className="text-parchment">{timeline.t_current}</span>
        </div>
      )}

      {events.length === 0 ? (
        <div className="text-mist text-sm text-center py-8">No events recorded</div>
      ) : (
        <div className="space-y-2">
          {events
            .sort(([, a], [, b]) => (b.day_index ?? 0) - (a.day_index ?? 0))
            .map(([id, event]) => (
              <div key={id} className="p-3 bg-void rounded-lg border border-stone/30">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="text-parchment text-sm font-medium">{event.title}</div>
                  {event.day_index !== undefined && (
                    <span className="text-xs text-mist/60">Day {event.day_index}</span>
                  )}
                </div>
                {event.participants.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {event.participants.map((p, i) => (
                      <span key={i} className="px-1.5 py-0.5 rounded text-xs bg-stone/40 text-mist font-mono">
                        {p}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function ThreadsView({ threads }: { threads: { open?: Record<string, { title: string; stakes?: string }>; closed?: Record<string, { title: string; resolution?: string }> } }) {
  const openThreads = Object.entries(threads.open ?? {});
  const closedThreads = Object.entries(threads.closed ?? {});

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-display text-parchment mb-3">Open Threads ({openThreads.length})</h3>
        {openThreads.length === 0 ? (
          <div className="text-mist text-sm">No open threads</div>
        ) : (
          <div className="space-y-2">
            {openThreads.map(([id, thread]) => (
              <div key={id} className="p-3 bg-void rounded-lg border border-rune/30">
                <div className="text-parchment text-sm font-medium mb-1">{thread.title}</div>
                <div className="text-mist/60 text-xs font-mono mb-1">{id}</div>
                {thread.stakes && <div className="text-mist text-xs">{thread.stakes}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-display text-parchment mb-3">Closed Threads ({closedThreads.length})</h3>
        {closedThreads.length === 0 ? (
          <div className="text-mist text-sm">No closed threads</div>
        ) : (
          <div className="space-y-2">
            {closedThreads.map(([id, thread]) => (
              <div key={id} className="p-3 bg-void rounded-lg border border-stone/30 opacity-75">
                <div className="text-parchment text-sm font-medium mb-1">{thread.title}</div>
                <div className="text-mist/60 text-xs font-mono mb-1">{id}</div>
                {thread.resolution && <div className="text-mist text-xs">{thread.resolution}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConstraintsView({ constraints }: { constraints: unknown }) {
  if (!constraints) {
    return <div className="text-mist text-sm text-center py-8">No constraints defined</div>;
  }

  if (!Array.isArray(constraints)) {
    return (
      <div className="p-3 bg-void rounded-lg border border-stone/30">
        <div className="text-xs font-mono overflow-auto">
          <JsonViewer data={constraints} expandAll />
        </div>
      </div>
    );
  }

  if (constraints.length === 0) {
    return <div className="text-mist text-sm text-center py-8">No constraints defined</div>;
  }

  return (
    <div className="space-y-2">
      {constraints.map((constraint, i) => (
        <div key={i} className="p-3 bg-void rounded-lg border border-stone/30">
          <div className="text-xs font-mono overflow-auto">
            <JsonViewer data={constraint} depth={1} />
          </div>
        </div>
      ))}
    </div>
  );
}
